import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Send, Mail, Phone, User, Paperclip, CheckCircle } from "lucide-react";
import { useContactForm, ContactFormData } from "@/hooks/useContactForm";
import { supabase } from "@/integrations/supabase/client";
import { FileUpload } from "@/components/shared/FileUpload";
import { FileReference } from "@/utils/temporaryFileStorage";
import { toast } from "sonner";

interface ContactCategory {
  id: string;
  name: string;
  description?: string;
}

const ContactForm = () => {
  const { submitContactForm, isSubmitting } = useContactForm();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<ContactCategory[]>([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    category_id: "",
  });
  const [attachments, setAttachments] = useState<FileReference[]>([]);

  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase
        .from("contact_categories")
        .select("id, name, description")
        .eq("is_active", true)
        .order("name");

      setCategories(data || []);

      // Auto-select category based on URL parameter
      const categoryParam = searchParams.get("category");
      if (categoryParam && data) {
        const matchingCategory = data.find(
          (cat) => cat.name.toLowerCase() === categoryParam.toLowerCase()
        );
        if (matchingCategory) {
          setFormData((prev) => ({
            ...prev,
            category_id: matchingCategory.id,
          }));
        }
      }
    };

    fetchCategories();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required category
    if (!formData.category_id) {
      toast.error("Please select a category for your inquiry.");
      return;
    }

    const result = await submitContactForm({
      ...formData,
      category_id: formData.category_id || undefined,
      attachments,
    });

    if (result.success) {
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        category_id: "",
      });
      setAttachments([]);
      setShowSuccessDialog(true);
    }
  };

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <AlertDialogTitle className="text-xl">
                Message Sent Successfully!
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              We've received your message and will respond within 24-48 hours. 
              You should also receive a confirmation email shortly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setShowSuccessDialog(false)}
              className="bg-green-600 hover:bg-green-700"
            >
              Great, thanks!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="w-full max-w-2xl mx-auto bg-card/50 backdrop-blur-sm border-border/50 shadow-xl">
        <CardHeader className="text-center space-y-2">
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
            Get in Touch
          </CardTitle>
          <p className="text-muted-foreground">
            We'd love to hear from you. Send us a message and we'll respond as
            soon as possible.
          </p>
        </CardHeader>

        <CardContent>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Selection */}
          {categories.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="category">What can we help you with? *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) =>
                  handleInputChange("category_id", value)
                }
              >
                <SelectTrigger
                  id="category"
                  className="border-border/50 focus:border-primary transition-colors"
                >
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border/50">
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Name *
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Your full name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
                className="border-border/50 focus:border-primary transition-colors"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                required
                className="border-border/50 focus:border-primary transition-colors"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Phone (Optional)
            </Label>
            <Input
              id="phone"
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              className="border-border/50 focus:border-primary transition-colors"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              type="text"
              placeholder="Brief description of your inquiry"
              value={formData.subject}
              onChange={(e) => handleInputChange("subject", e.target.value)}
              required
              className="border-border/50 focus:border-primary transition-colors"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              placeholder="Please provide details about your inquiry..."
              value={formData.message}
              onChange={(e) => handleInputChange("message", e.target.value)}
              required
              rows={6}
              className="border-border/50 focus:border-primary transition-colors resize-none bg-background text-foreground"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              Attachments (Optional)
            </Label>
            <FileUpload
              onFileChange={setAttachments}
              existingFiles={attachments}
              maxFiles={3}
              maxFileSize={10}
              acceptedTypes={[
                ".pdf",
                ".jpg",
                ".jpeg",
                ".png",
                ".doc",
                ".docx",
                ".txt",
              ]}
              className="bg-background/50"
            />
            <p className="text-xs text-muted-foreground">
              You can attach up to 3 files to help us better understand your
              inquiry.
            </p>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-nation-teal hover:bg-nation-teal/90 text-white shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Sending...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Send className="h-4 w-4" />
                Send Message
              </div>
            )}
          </Button>
        </form>
        </CardContent>
      </Card>
    </>
  );
};

export default ContactForm;
